import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { useTranslation } from 'react-i18next'
import VacationTable from '../../components/Table/Vacation/VacationTable'
import { Box, Button, Collapse, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/redux/store'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { useGetEmployeeVacationsQuery } from '../../store/redux/reducers/vacationSlice'
import { FilterList, Person, Work } from '@mui/icons-material'
import FilterVacationsMenu, { SearchEmployee } from '../../components/menu/FilterVacationsMenu'
import { EmployeeVacation, VacationRequestProps } from '../../store/types/utils/VacationTypes'
import { StyledPageHeaderContainer } from '../../styles/PageHeaderStyles'
import { StyledPageContent } from '../../styles/PageStyles'
import Loading from '../../components/Loading/Loading'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import HeaderCountLabel from '../../components/Page/HeaderCountLabel'

const VacationView = () => {
  const { t } = useTranslation()
  const [isFilteringMenuOpen, setIsFilteringMenuOpen] = useState(false)
  const selectedDateFromState = useSelector((state: RootState) => getSelectedDate(state))
  const selectedDate = useMemo(() => dayjs(selectedDateFromState), [selectedDateFromState])
  const currentUser = useSelector(getCurrentUser)
  const [requestProps, setRequestProps] = useState<VacationRequestProps>({})
  const [chosenDate, setChosenDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'))
  const [searchEmployees, setSearchEmployees] = useState<SearchEmployee[]>([])
  const boxRef = useRef(null)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()
  const isDownXsMid = useMediaQuery(theme.breakpoints.down('xsMid'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setPageName(t('page.name.vacations')))
    dispatch(setPageIndex(PageIndex.VACATION))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: true,
        infoButton: false,
      })
    )
    dispatch(monthPickerActions.setDate(dayjs().toString()))

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  useEffect(() => {
    if (selectedDate.isValid()) {
      setRequestProps(prev => ({
        ...prev,
        start: dayjs(selectedDate).startOf('month').format('YYYY-MM-DD'),
        end: dayjs(selectedDate).endOf('month').format('YYYY-MM-DD'),
      }))
    }
  }, [selectedDate])

  const { data: filterEmpData, isLoading } = useGetEmployeeVacationsQuery(requestProps, {
    skip: !requestProps.start || !requestProps.end,
  })

  useEffect(() => {
    if (searchEmployees.length === 0 && filterEmpData !== undefined) {
      setSearchEmployees(
        filterEmpData.map((data: EmployeeVacation) => ({
          id: data.id,
          name: data.name,
        }))
      )
    }
  }, [searchEmployees.length, filterEmpData])

  const employeesOnVacationCount = useMemo(() => {
    if (!filterEmpData) {
      return 0
    }

    if (chosenDate) {
      return filterEmpData.filter((employee: EmployeeVacation) =>
        employee.vacation.some(vacation =>
          dayjs(chosenDate).isBetween(vacation.startDate, vacation.endDate, undefined, '[]')
        )
      ).length
    }

    const employeesOnVacation = filterEmpData.filter((employee: EmployeeVacation) => {
      for (const vacation of employee.vacation) {
        const startDate = dayjs(vacation.startDate)
        const endDate = dayjs(vacation.endDate)

        if (
          (startDate.isBefore(selectedDate.endOf('month')) && endDate.isAfter(selectedDate.startOf('month'))) ||
          startDate.isSame(selectedDate.endOf('month')) ||
          endDate.isSame(selectedDate.startOf('month'))
        ) {
          return true
        }
      }
      return false
    })

    return employeesOnVacation.length
  }, [filterEmpData, selectedDate, chosenDate])

  const workdaysThisMonthCount = useMemo(() => {
    const firstDayOfMonth = selectedDate.startOf('month')
    const lastDayOfMonth = selectedDate.endOf('month')
    let workdaysCount = 0

    for (let currentDay = firstDayOfMonth; currentDay.isBefore(lastDayOfMonth); currentDay = currentDay.add(1, 'day')) {
      if (currentDay.day() !== 0 && currentDay.day() !== 6) {
        workdaysCount++
      }
    }

    return workdaysCount
  }, [selectedDate])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickableElements = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL']
      const autocompleteClassNames = ['MuiAutocomplete-option', 'MuiAutocomplete-root']
      const dialogClassNames = ['MuiDialog-root', 'MuiDialog-container']
      const target = event.target as HTMLElement
      const isClickableElement =
        clickableElements.includes(target.tagName) ||
        autocompleteClassNames.some(className => target.classList.contains(className)) ||
        dialogClassNames.some(className => target.classList.contains(className))
      const isDialogOpen = document.querySelector('.vacation-dialog-open')
      const isWithinBox = boxRef.current && (boxRef.current as HTMLElement).contains(target)
      const isWithinFilterMenu = filterMenuRef.current && filterMenuRef.current.contains(target)
      const isWithinDialog = dialogRef.current && dialogRef.current.contains(target)
      if (!isWithinBox && !isClickableElement && !isWithinFilterMenu && !isWithinDialog && !isDialogOpen) {
        setChosenDate(dayjs().format('YYYY-MM-DD'))
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        filterEmpData && (
          <>
            <StyledPageHeaderContainer sx={{ px: isDownSm ? 0 : 2, height: 'fit-content' }}>
              {!isDownSm ? (
                <Stack
                  direction={'row'}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                  className={'set-null-date'}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<FilterList />}
                    className={`${isFilteringMenuOpen ? 'selected' : ''}`}
                    onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
                    fullWidth={isDownSm}
                  >
                    {t('page.users.buttons.filter')}
                  </Button>

                  <Stack
                    direction={isDownMd ? 'column' : 'row'}
                    sx={{
                      width: !isDownMd ? '75%' : 'fit-content',
                      justifyContent: 'space-evenly',
                      gap: 2,
                    }}
                  >
                    <HeaderCountLabel label={t('page.vacationView.empOnVac')} count={employeesOnVacationCount} />
                    <HeaderCountLabel label={t('page.vacationView.workDays')} count={workdaysThisMonthCount} />
                  </Stack>
                </Stack>
              ) : (
                <Stack
                  direction={'column'}
                  sx={{
                    alignItems: 'center',
                    width: '100%',
                    gap: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack direction={isDownXsMid ? 'row' : 'column'}>
                      {isDownXsMid ? (
                        <>
                          <Tooltip title={<Typography>{t('page.vacationView.empOnVac')}</Typography>} arrow>
                            <Person sx={{ mr: 0.5, mt: '1px' }} />
                          </Tooltip>
                          <Typography variant="h5" noWrap>
                            {employeesOnVacationCount}
                          </Typography>
                        </>
                      ) : (
                        <HeaderCountLabel label={t('page.vacationView.empOnVac')} count={employeesOnVacationCount} />
                      )}
                    </Stack>
                    <Stack direction={isDownXsMid ? 'row' : 'column'}>
                      {isDownXsMid ? (
                        <>
                          <Tooltip title={<Typography>{t('page.vacationView.workDays')}</Typography>} arrow>
                            <Work sx={{ mr: 0.5 }} />
                          </Tooltip>
                          <Typography variant="h5" noWrap>
                            {workdaysThisMonthCount}
                          </Typography>
                        </>
                      ) : (
                        <HeaderCountLabel label={t('page.vacationView.workDays')} count={workdaysThisMonthCount} />
                      )}
                    </Stack>
                  </Stack>

                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<FilterList />}
                    className={`${isFilteringMenuOpen ? 'selected' : ''}`}
                    onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
                    sx={{ alignSelf: 'flex-start' }}
                    fullWidth
                  >
                    {t('page.users.buttons.filter')}
                  </Button>
                </Stack>
              )}
            </StyledPageHeaderContainer>
            <StyledPageContent>
              {!isDownMd ? (
                <Box
                  ref={boxRef}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: isFilteringMenuOpen ? 2 : 0,
                    paddingBottom: 3,
                  }}
                >
                  <Collapse
                    in={isFilteringMenuOpen}
                    orientation="horizontal"
                    sx={{ marginRight: isFilteringMenuOpen ? 1 : 0 }}
                    unmountOnExit
                  >
                    {currentUser && (
                      <FilterVacationsMenu
                        isOpen={isFilteringMenuOpen}
                        currentUser={currentUser}
                        setRequestProps={setRequestProps}
                        requestProps={requestProps}
                        ref={filterMenuRef}
                        searchEmployees={searchEmployees}
                      />
                    )}
                  </Collapse>

                  {filterEmpData && (
                    <VacationTable
                      dialogReference={dialogRef}
                      isFiltersOpen={!isFilteringMenuOpen}
                      empData={filterEmpData}
                      chosenDate={chosenDate}
                      setChosenDate={setChosenDate}
                      getEmployeeRequestProps={requestProps}
                    />
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isFilteringMenuOpen ? 2 : 0,
                    height: 'fit-content',
                    paddingBottom: 3,
                  }}
                >
                  <Collapse in={isFilteringMenuOpen} orientation="vertical">
                    {currentUser && (
                      <FilterVacationsMenu
                        isOpen={isFilteringMenuOpen}
                        currentUser={currentUser}
                        setRequestProps={setRequestProps}
                        requestProps={requestProps}
                        searchEmployees={searchEmployees}
                      />
                    )}
                  </Collapse>

                  {filterEmpData && (
                    <VacationTable
                      isFiltersOpen={!isFilteringMenuOpen}
                      empData={filterEmpData}
                      chosenDate={chosenDate}
                      setChosenDate={setChosenDate}
                      dialogReference={dialogRef}
                      getEmployeeRequestProps={requestProps}
                    />
                  )}
                </Box>
              )}
            </StyledPageContent>
          </>
        )
      )}
    </>
  )
}

export default VacationView
